import React from "react";
import { useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Avatar,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { ReactComponent as Edit } from "../../../../../assets/images/edit.svg";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import styles from "./style";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../../../../components/CustomPagination";
import Modal from "../../../../../components/Modal";
import { formatDate } from "../../../../../utils/DateFormat";

// ----------------------------------------------------------------
// Employees Loan list
// ----------------------------------------------------------------

const LoanList = ({
  data,
  page,
  setPage,
  total,
  handleDelete,
  setFrom,
  setOpen,
  setItem,
  setChange,
  permission,
  role
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  // ---------------------------------------
  // If you click on edit icon
  // ---------------------------------------
  const handleNavigate = (item) => {
    setItem(item);
    setOpen(true);
    setFrom("edit");
    setChange(false);
  };

  const handleNavigateView = (item) => {
    navigate("/dashboard/viewLoan", { state: { item } });
  };
  const [opens, setOpens] = useState(false);
  const [value, setValue] = useState();

  const deleteData = (item) => {
    setValue(item);
  };
  const handleDeleteItem = () => {
    handleDelete(value); // Call the delete function passed as prop
    setOpens(false); // Close the modal after deleting
  };

  // Status chip styling
  const getStatusChipStyles = (status) => {
    if (!status) {
      return {
        backgroundColor: "default",
        color: "inherit"
      };
    }
    const statusLower = status.toLowerCase();
    if (statusLower === "active" || statusLower === "open") {
      return {
        backgroundColor: "#cef5d6",
        color: "#126300"
      };
    }
    if (statusLower === "closed") {
      return {
        backgroundColor: "#f5f5f5",
        color: "#666"
      };
    }
    if (statusLower === "complete" || statusLower === "completed") {
      return {
        backgroundColor: "#d4edda",
        color: "#155724"
      };
    }
    if (statusLower === "pending") {
      return {
        backgroundColor: "#fff3cd",
        color: "#856404"
      };
    }
    return {
      backgroundColor: "default",
      color: "inherit"
    };
  };

  // Check if status is closed or complete
  const isStatusClosedOrComplete = (status) => {
    const statusLower = status?.toLowerCase();
    return statusLower === "closed" || statusLower === "complete" || statusLower === "completed";
  };

  // Check if loan has any transactions (payment started)
  const hasTransactions = (item) => {
    // If leftBalance is different from loanAmt, payment has started
    // Or if paidAmount exists and is greater than 0
    const loanAmount = Number(item?.loanAmt) || 0;
    const leftBalance = Number(item?.leftBalance);
    const paidAmount = Number(item?.paidAmount) || 0;
    
    // Check if any payment has been made
    if (paidAmount > 0) return true;
    if (leftBalance !== undefined && leftBalance !== null && leftBalance < loanAmount) return true;
    if (item?.transactionCount > 0) return true;
    
    return false;
  };

  // Check if edit should be disabled
  const isEditDisabled = (item) => {
    return isStatusClosedOrComplete(item?.status) || hasTransactions(item);
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.TableHead}>Name</TableCell>
              <TableCell sx={styles.TableHead}>Issued Date</TableCell>
              <TableCell sx={styles.TableHead}>Tax Year</TableCell>
              <TableCell sx={styles.TableHead}>Start Period</TableCell>
              <TableCell sx={styles.TableHead}>Loan Amount</TableCell>
              <TableCell sx={styles.TableHead}>Emi Amount</TableCell>
              <TableCell sx={styles.TableHead}>Left Balance</TableCell>
              <TableCell sx={styles.TableHead}>Status</TableCell>
              <TableCell sx={styles.TableHead}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="h6">No data</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell sx={styles.tableAvtardata}>
                    <Avatar
                      src={item?.employeeDetails?.personalInfo?.image}
                      sx={{ mr: "8px" }}
                    />
                    <Box>
                      <Typography sx={styles.name}>
                        {item?.employeeDetails?.personalInfo?.firstName}{" "}
                        {item?.employeeDetails?.personalInfo?.surName}
                      </Typography>
                      <Typography sx={styles.mail}>{item?.email}</Typography>
                    </Box>{" "}
                  </TableCell>
                  <TableCell sx={styles.tabledata}>
                    {item?.createdAt ? formatDate(item.createdAt) : "-"}
                  </TableCell>
                  <TableCell sx={styles.tabledata}>
                    {formatDate(item?.taxYear)}
                  </TableCell>
                  <TableCell sx={styles.tabledata}>
                    {formatDate(item?.repayStart)}
                  </TableCell>
                  <TableCell sx={styles.tabledata}>{item?.loanAmt ?? "-"}</TableCell>
                  <TableCell sx={styles.tabledata}>{item?.repayAmt ?? "-"}</TableCell>
                  <TableCell sx={styles.tabledata}>{item?.leftBalance ?? "-"}</TableCell>
                  <TableCell sx={styles.tabledata}>
                    <Chip
                      label={item?.status || "-"}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        textTransform: "capitalize",
                        ...getStatusChipStyles(item?.status)
                      }}
                    />
                  </TableCell>
                  <TableCell sx={styles.tabledata}>
                    <Box sx={styles.tableActiondata}>
                      {(permission?.loan?.isEdit || role === "Admin" || role === "SuperAdmin") ? (
                        <Edit
                          onClick={() => {
                            if (!isEditDisabled(item)) {
                              handleNavigate(item);
                            }
                          }}
                          style={{
                            opacity: isEditDisabled(item) ? 0.3 : 1,
                            cursor: isEditDisabled(item) ? "not-allowed" : "pointer",
                            pointerEvents: isEditDisabled(item) ? "none" : "auto"
                          }}
                        />
                      ) : null}
                      <RemoveRedEyeOutlinedIcon
                        onClick={() => handleNavigateView(item)}
                        sx={styles.viewIcon}
                      />
                      {(permission?.loan?.isEdit || role === "Admin" || role === "SuperAdmin") ? <DeleteOutlineIcon
                        sx={{ color: "#ff0000" }}
                        onClick={() => {
                          deleteData(item?._id), setOpens(true);
                        }}
                      /> : null}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data && (
          <CustomPagination
            pageCount={total}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalItems={data.length}
          />
        )}
      </TableContainer>
      <Modal
        open={opens}
        setOpen={setOpens}
        deletetText={"Delete"}
        cancelText={"Cancel"}
        title={"Delete Loan"}
        handleDelete={handleDeleteItem}
      >
        <Box>
          <Typography>Are you sure you want to Delete?</Typography>
        </Box>
      </Modal>{" "}
    </>
  );
};
export default LoanList;
